import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTh, faList, faTimes } from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../General/Tooltip';
import { searchProductMentionBrands } from '../../api/product_mentions';
import { getAllIndustries } from '../../api/industries';
import { getAllDepartments } from '../../api/departments';
import { METHOD_META, CONFIDENCE_BANDS, PLATFORMS, CREATOR_TIERS, ADMIN_QUALITY_GRADES, UNSCORED_TAB, getCategoryFilterGroups } from '../../helpers/product_mention_helpers';
import './ProductMentionsControls.scss';

const SEARCH_DEBOUNCE_MS = 350;
const BRAND_QUERY_MIN_CHARS = 2;

const ProductMentionsControls = props => {
  const { filters, onChange, methodCounts, view, onView, hideSignalFilters, hideProductFilters, isUnscored, unscoredCount, onToggleUnscored } = props;

  const [q, setQ] = useState(filters.q);
  const debounceRef = useRef(null);
  const changeQuery = value => {
    setQ(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange({ q: value.trim() }), SEARCH_DEBOUNCE_MS);
  };

  // Brand typeahead — a null result list means the dropdown is closed. The
  // search id drops stale responses from a superseded query.
  const [brandQuery, setBrandQuery] = useState('');
  const [brandResults, setBrandResults] = useState(null);
  const [brandHighlightIndex, setBrandHighlightIndex] = useState(0);
  const brandDebounceRef = useRef(null);
  const brandSearchIdRef = useRef(0);
  const changeBrandQuery = value => {
    setBrandQuery(value);
    clearTimeout(brandDebounceRef.current);
    const searchId = ++brandSearchIdRef.current;
    if (value.trim().length < BRAND_QUERY_MIN_CHARS) return setBrandResults(null);
    brandDebounceRef.current = setTimeout(() => {
      searchProductMentionBrands(value.trim())
        .then(data => {
          if (searchId !== brandSearchIdRef.current) return;
          setBrandResults(data.brands);
          setBrandHighlightIndex(0);
        })
        .catch(err => window.ALERT.error(err));
    }, SEARCH_DEBOUNCE_MS);
  };
  // Picks fire on mousedown so they beat the input blur that closes the list.
  const pickBrand = brand => {
    onChange({ brand });
    setBrandQuery('');
    setBrandResults(null);
  };
  const closeBrandResults = () => {
    clearTimeout(brandDebounceRef.current);
    brandSearchIdRef.current += 1;
    setBrandResults(null);
  };
  const onBrandKeyDown = e => {
    if (!brandResults) return;
    if (e.key === 'Escape') return closeBrandResults();
    if (!brandResults.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setBrandHighlightIndex(prev => (prev + step + brandResults.length) % brandResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pickBrand(brandResults[brandHighlightIndex]);
    }
  };
  // Attached to whichever row holds the keyboard highlight so it stays
  // visible as arrow keys move it through the scrollable list.
  const scrollHighlightIntoView = element => element?.scrollIntoView({ block: 'nearest' });
  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(brandDebounceRef.current);
  }, []);

  const [categoryGroups, setCategoryGroups] = useState([]);
  useEffect(() => {
    Promise.all([getAllIndustries(), getAllDepartments()])
      .then(([industries, departments]) => setCategoryGroups(getCategoryFilterGroups(industries, departments)))
      .catch(() => window.ALERT.error('Failed to load the category filter.'));
  }, []);

  const methodOptions = methodCounts
    .filter(entry => entry.method)
    .map(entry => ({
      key: entry.method,
      display: METHOD_META[entry.method]?.display || entry.method,
      count: entry.count
    }));

  const toggle = (key, value) => {
    const list = filters[key];
    onChange({ [key]: list.includes(value) ? list.filter(other => other !== value) : [...list, value] });
  };

  return (
    <div className='product-mentions-controls'>
      <div className='pm-filter-row'>
        <span className='pm-filter-heading'>Platform</span>
        {PLATFORMS.map(({ key, display }) => (
          <button type='button' key={key} className={cn('pm-chip', { active: filters.platforms.includes(key) })} onClick={() => toggle('platforms', key)}>
            {display}
          </button>
        ))}
        {!hideSignalFilters && (
          <>
            <span className='pm-filter-heading'>Method</span>
            {methodOptions.map(({ key, display, count }) => (
              <button type='button' key={key} className={cn('pm-chip', { active: filters.methods.includes(key) })} onClick={() => toggle('methods', key)}>
                {display}
                <span className='pm-chip-count'>{count}</span>
              </button>
            ))}
            <span className='pm-filter-heading'>Confidence</span>
            {CONFIDENCE_BANDS.map(({ key, display, detail }) => (
              <Tooltip key={key} message={detail} outerClassName='pm-chip-tip'>
                <button type='button' className={cn('pm-chip', { active: filters.bands.includes(key) })} onClick={() => toggle('bands', key)}>
                  {display}
                </button>
              </Tooltip>
            ))}
            <span className='pm-filter-heading'>Score</span>
            <button type='button' className={cn('pm-chip', { active: isUnscored })} onClick={onToggleUnscored}>
              {UNSCORED_TAB.display}
              <span className='pm-chip-count'>{unscoredCount}</span>
            </button>
            {ADMIN_QUALITY_GRADES.map(({ value, label, title }) => (
              <Tooltip key={value} message={title} outerClassName='pm-chip-tip'>
                <button type='button' className={cn('pm-chip', { active: filters.scores.includes(value) })} onClick={() => toggle('scores', value)}>
                  {label}
                </button>
              </Tooltip>
            ))}
          </>
        )}

        <div className='pm-filter-tools'>
          <select
            className={cn('pm-filter-select', { active: filters.tiers.length })}
            value={filters.tiers[0] ?? ''}
            onChange={e => onChange({ tiers: e.target.value ? [Number(e.target.value)] : [] })}
          >
            <option value=''>All tiers</option>
            {CREATOR_TIERS.map(({ key, display }) => (
              <option key={key} value={key}>
                {display}
              </option>
            ))}
          </select>
          {!hideProductFilters && (
            <>
              {filters.brand ? (
                <button type='button' className='pm-brand-pill' onClick={() => onChange({ brand: null })} title='Clear brand filter'>
                  {filters.brand.name}
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              ) : (
                <div className='pm-brand-filter'>
                  <input
                    value={brandQuery}
                    onChange={e => changeBrandQuery(e.target.value)}
                    onFocus={() => changeBrandQuery(brandQuery)}
                    onBlur={closeBrandResults}
                    onKeyDown={onBrandKeyDown}
                    placeholder='Filter by brand'
                    className='pm-brand-input'
                  />
                  {brandResults && (
                    <div className='pm-brand-results'>
                      {brandResults.map((brand, index) => (
                        <button
                          type='button'
                          key={brand.id}
                          ref={index === brandHighlightIndex ? scrollHighlightIntoView : null}
                          className={cn('pm-brand-result', { highlighted: index === brandHighlightIndex })}
                          onMouseDown={() => pickBrand(brand)}
                        >
                          <span className='pm-brand-result-name'>{brand.name}</span>
                          <span className='pm-brand-result-count'>{brand.mentionCount}</span>
                        </button>
                      ))}
                      {!brandResults.length && <div className='pm-brand-result empty'>No brands with mentions match</div>}
                    </div>
                  )}
                </div>
              )}
              <select
                className={cn('pm-filter-select', { active: filters.category })}
                value={filters.category}
                onChange={e => onChange({ category: e.target.value })}
              >
                <option value=''>All categories</option>
                {categoryGroups.map(({ label, options }) => (
                  <optgroup key={label} label={label}>
                    {options.map(({ value, display }) => (
                      <option key={value} value={value}>
                        {display}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </>
          )}
          <input value={q} onChange={e => changeQuery(e.target.value)} placeholder='Search handles, products, brands' className='pm-search' />
          <div className='pm-view-toggle'>
            <button type='button' className={cn({ active: view === 'grid' })} onClick={() => onView('grid')} aria-label='Grid view' title='Grid'>
              <FontAwesomeIcon icon={faTh} />
            </button>
            <button type='button' className={cn({ active: view === 'table' })} onClick={() => onView('table')} aria-label='Table view' title='Table'>
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductMentionsControls;
